import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData, applyAutoSlip } from "@shared/utils/CommonUtils";
import { global, globalMenu } from "@shared/libs/Global";
import ExcelUpload from "@widgets/commonpopup/ExcelUpload";
import { showPopupModal } from "@widgets/CommonModal";
import SharedForm from "../../../../../widgets/SharedForm";
import { fnSave } from "@shared/api/restApi";
import SearchPanel from "../../../../../widgets/SearchPanel";
import { menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { dateToString } from "@shared/utils/DateUtils";
import SnFileHelper from "@shared/components/SnFileHelper";
import { SnTab } from "@shared/components/SnTab";
import { SnTabHeader } from "@shared/components/SnTabHeader";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_PTY_REG_002 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();
  const fileHelperRef = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
    grid2: React.createRef(),
    sheet1: React.createRef(),
  });
  const sharedFormRef1 = useRef<any>();
  const sharedFormRef2 = useRef<any>();

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
    grid2: [],
  });
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [sharedForm2, setSharedForm2] = useState<any[]>([]);

  const [focusedRowData, setFocusedRowData] = useState<any>();
  const [cdIoList, setCdIoList] = useState<any[]>([]); // onValueChaged 함수에서 수불형태 비교용 데이터
  const [selectedTab, setSelectedTab] = useState<string>("1");
  const [insertedNode, setInsertedNode] = useState<any>(null);

  useImperativeHandle(ref, () => ({
    onSearch,
    onInsert,
    onDelete,
    onSave,
  }));

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setSharedForm(rtn.form.freeform1);
        setSharedForm2(rtn.form.freeform2);
        setColums({
          grid1: rtn.grid.grid1,
          grid2: rtn.grid.grid2,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  const onSearch = async () => {
    try {
      const modifiedRows = gridRefs.current["grid1"].current.getChange();
      if (modifiedRows.modifiedRowcount > 0) {
        const confirm = await showDialog(
          "confirm",
          "수정된 내용이 있습니다. 저장하시겠습니까?"
        );
        if (confirm) {
          const isSaved = await onSave();
          if (!isSaved) return;
        }
      }

      const params = {
        id: "sn_mtl_pty_reg_002_01",
        params: searchRef.current.getFormdata(),
      };

      setFocusedRowData(null);
      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data[0],
        grid2: data[1],
      });
    } catch (error: any) {}
  };

  const onInsert = () => {
    const searchFormData = searchRef.current.getFormdata();
    const selectedData = {
      tp_property: searchFormData.tp_property,
      st_property: searchFormData.st_property,
    };

    const grid1 = gridRefs.current["grid1"]?.current;
    const grid2 = gridRefs.current["grid2"]?.current;

    const focusedRowNode = grid1?.focusedRowNode();
    const focusedData = focusedRowNode?.data;

    let st_prop = focusedData?.st_property ?? selectedData.st_property;

    if (st_prop !== "STOCK" && st_prop !== "USE") return;

    if (st_prop === "STOCK") setSelectedTab("1");
    else if (st_prop === "USE") setSelectedTab("2");

    let lastRowData: any = null;

    // USE면 마지막 row 가져오기
    if (st_prop === "USE") {
      const rowCount = grid2.getDisplayedRowCount();

      if (rowCount > 0) {
        const lastNode = grid2.getRowNodeAtIndex(rowCount - 1);
        lastRowData = lastNode?.data;

        if (lastNode) {
          lastNode.setDataValue("yn_assign", "N");
        }
      }
    }

    const newRow = {
      cd_firm: userInfo.cd_firm,
      cd_property: focusedRowNode.data.cd_property,
      // dt_assign:
      //   st_prop === "STOCK"
      //     ? dateToString(new Date())
      //     : st_prop === "USE"
      //       ? (lastRowData?.dt_assign ?? null)
      //       : null,
      dt_assign: dateToString(new Date()),
      dt_return: st_prop === "USE" ? dateToString(new Date()) : null,
      yn_assign: st_prop === "STOCK" ? "Y" : st_prop === "USE" ? "N" : null,
    };

    const addedNode = grid2?.addRow(newRow);

    if (addedNode && addedNode.data) {
      setFocusedRowData(addedNode.data);
      setInsertedNode(addedNode);
      applyAssignValues(addedNode, selectedTab);
    }
  };

  useEffect(() => {
    if (!insertedNode) return;

    applyAssignValues(insertedNode, selectedTab);
  }, [selectedTab]);

  const applyAssignValues = (node: any, tab: string) => {
    if (!node) return;

    if (tab === "1") {
      // STOCK (할당)
      node.setDataValue("yn_assign", "Y");
      node.setDataValue("dt_return", null);
      node.setDataValue("dt_assign", dateToString(new Date()));
    } else if (tab === "2") {
      // USE (회수)
      node.setDataValue("yn_assign", "N");
      node.setDataValue("dt_return", dateToString(new Date()));
    }
  };

  const onDelete = async () => {
    if (gridRefs.current["grid2"].current.getDisplayedRowCount() < 1) return;

    const focusedRowNode = gridRefs.current["grid2"]?.current.focusedRowNode();
    let rowIndex: any = 0;
    if (focusedRowNode) rowIndex = focusedRowNode.rowIndex;

    gridRefs.current["grid2"].current?.deleteSelectedRows();
    if (rowIndex === 0) {
      setFocusedRowData(null);
    } else {
      const newRowIndex = rowIndex === 0 ? 0 : rowIndex - 1;
      gridRefs.current["grid2"].current?.selectAndFocusRow(newRowIndex);
    }
  };

  const onSave = async () => {
    let assignChanges;
    let propertyChanges;
    try {
      gridRefs.current["grid2"].current.stopEditing();

      const requiredValuesCheck =
        gridRefs.current["grid2"].current.requiredValuesCheck();
      if (!requiredValuesCheck) return false;

      propertyChanges = gridRefs.current["grid1"].current.getChange();
      assignChanges = gridRefs.current["grid2"].current.getChange();
      if (assignChanges.modifiedRowcount == 0) {
        await showDialog("alter", "저장할 내용이 없습니다.");
        return false;
      }

      const focusedRowNodeH =
        gridRefs.current["grid2"].current.focusedRowNode();
      const noSlip = await applyAutoSlip({
        cd_column: "no_assign",
        cd_module: Menuprops.cd_module,
        no_line: 35,
        dt: dateToString(new Date()),
        girdrefs: [gridRefs.current["grid2"]],
        inserts: [assignChanges],
        cd_menu: Menuprops.cd_menu,
      });

      const saveData = {
        property: propertyChanges,
        assign: assignChanges,
      };
      const ok = await fnSave("SN_MTL_PTY_REG_002_SAVE_01", saveData);
      if (!ok) return false;

      gridRefs.current["grid1"].current.resetUpdate();
      gridRefs.current["grid2"].current.resetUpdate();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current.resetUpdate(true);
      gridRefs.current["grid2"].current.resetUpdate(true);
      return false;
    }
  };

  const onTabChanged = (index: string) => {
    setSelectedTab(index);
  };

  const onValueChaged = async (data: any, param: string, formData: any) => {
    switch (param) {
      case "cd_assign": {
        const value = String(data ?? "");
        const first = value.charAt(0);

        const grid1 = gridRefs.current["grid1"]?.current;
        const focusedNode = grid1?.focusedRowNode();

        if (!focusedNode) return {};

        if (first === "1") {
          focusedNode.setDataValue("st_property", "USE", "noEvent");
        } else if (first === "2") {
          focusedNode.setDataValue("st_property", "STOCK", "noEvent");
        }
        return {};
      }
      case "cd_emp": {
        const value = String(data.cd_emp ?? "");
        const grid1 = gridRefs.current["grid1"]?.current;
        const focusedNode = grid1?.focusedRowNode();
        if (value !== "") {
          focusedNode.setDataValue("cd_emp", value, "noEvent");
          focusedNode.setDataValue("cd_location", "USER", "noEvent");
          return {
            cd_emp: value,
            cd_location_return: "USER", // 👈 여기 추가
          };
        } else {
          focusedNode.setDataValue("cd_emp", value, "noEvent");
          focusedNode.setDataValue("cd_location", "", "noEvent");
          return {
            cd_emp: value,
            cd_location_return: "", // 👈 여기 추가
          };
        }
      }
      case "cd_location": {
        const value = String(data ?? "");

        const grid1 = gridRefs.current["grid1"]?.current;
        const focusedNode = grid1?.focusedRowNode();

        if (!focusedNode) return {};
        focusedNode.setDataValue("cd_location", value, "noEvent");
        return {};
      }
      default:
        return {};
    }
  };

  const onValueChaged2 = async (data: any, param: string, formData: any) => {
    switch (param) {
      case "cd_assign": {
        const value = String(data ?? "");
        const first = value.charAt(0);

        const grid1 = gridRefs.current["grid1"]?.current;
        const focusedNode = grid1?.focusedRowNode();

        if (!focusedNode) return {};

        if (first === "1") {
          focusedNode.setDataValue("st_property", "USE", "noEvent");
        } else if (first === "2") {
          focusedNode.setDataValue("st_property", "STOCK", "noEvent");
        }

        return {};
      }
      case "cd_location_return": {
        const value = String(data ?? "");

        const grid1 = gridRefs.current["grid1"]?.current;
        const focusedNode = grid1?.focusedRowNode();

        if (!focusedNode) return {};
        focusedNode.setDataValue("cd_location", value, "noEvent");
        focusedNode.setDataValue("cd_emp", "", "noEvent");
        return {};
      }

      default:
        return {};
    }
  };

  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          //onFocusedRowChanged,
        },
      },
      grid2: {
        columns: columns.grid2,
        dataSource: dataSources.grid2,
        // events: {
        //   onFocusedRowChanged: onFocusedRowChanged2,
        //   onBeforeAddRow: onBeforeAddRow2,
        // },
      },
      sheet1: {
        columns: columns.sheet1,
        dataSource: dataSources.sheet1,
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>

      <div className="w-full flex h-full min-h-0 space-x-1.5">
        {/* 왼쪽 GRID1 */}
        <SnResizeBar direction={"width"} id="panel1" orgSize="23.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{
              rowSelection: "single",
              title: "비품정보",
              isEditable: false,
            }}
            events={grids.grid1.events}
            filter={{
              refs: [gridRefs.current["grid2"]],
              columns: ["cd_property"],
            }}
          />
        </SnResizeBar>

        {/* 오른쪽 영역 */}
        <SnResizeBar direction={"width"} id="panel2" orgSize="76.5%">
          <div className="flex flex-col h-full min-h-0">
            {/* 상단 TAB */}
            <div className="h-1/3 min-h-0">
              <SnTab
                titles={["할당", "회수"]}
                width={"100%"}
                header={[<SnTabHeader />, <SnTabHeader />]}
                selectedTab={selectedTab}
                onTabChanged={onTabChanged}
                body={[
                  <SharedForm
                    ref={sharedFormRef1}
                    data={focusedRowData}
                    controls={sharedForm}
                    gridref={gridRefs.current["grid2"]}
                    onValueChaged={onValueChaged}
                  />,
                  <SharedForm
                    ref={sharedFormRef2}
                    data={focusedRowData}
                    controls={sharedForm2}
                    gridref={gridRefs.current["grid2"]}
                    onValueChaged={onValueChaged2}
                  />,
                ]}
              />
            </div>

            {/* 하단 GRID2 */}
            <div className="h-2/3 min-h-0">
              <SnGrid
                ref={gridRefs.current["grid2"]}
                columnConfig={grids.grid2.columns}
                rowData={grids.grid2.dataSource}
                etcProps={{ rowSelection: "single" }}
              />
            </div>
          </div>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_MTL_PTY_REG_002;
